import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@hipotecar/ui/lib/utils";
import { useSidebar } from "../hooks/useSidebar";
import { isChatEnabled } from "@/config/features";
import { ReactComponent as PlusIcon } from "@assets/icons/nav/plus.svg?react";
import { ReactComponent as BuildingIcon } from "@assets/icons/nav/building.svg?react";
import { ReactComponent as DollarSquareIcon } from "@assets/icons/nav/dollar-square.svg?react";
import { ReactComponent as MessageIcon } from "@assets/icons/nav/message.svg?react";

type NavItem = {
	nameKey: string;
	icon: React.ReactNode;
	path: string;
	/** Exact match only; otherwise nested routes keep the item active. */
	exact?: boolean;
	/** Hides the item while it returns false. */
	enabled?: () => boolean;
};

const navItems: NavItem[] = [
	{ icon: <PlusIcon />, nameKey: "sidebar.new", path: "/new", exact: true },
	{ icon: <BuildingIcon />, nameKey: "sidebar.properties", path: "/properties" },
	{ icon: <DollarSquareIcon />, nameKey: "sidebar.valuate", path: "/valuar" },
	{ icon: <MessageIcon />, nameKey: "sidebar.robotin", path: "/chat", enabled: isChatEnabled },
];

const AppSidebar: React.FC = () => {
	const { t } = useTranslation();
	const { isExpanded, isMobileOpen, toggleMobileSidebar } = useSidebar();
	const { pathname } = useLocation();
	const showLabels = isExpanded || isMobileOpen;

	const isActive = ({ path, exact }: NavItem) =>
		exact ? pathname === path : pathname === path || pathname.startsWith(`${path}/`);

	const handleMobileLinkClick = () => {
		if (isMobileOpen) {
			toggleMobileSidebar();
		}
	};

	return (
		<aside
			className={cn(
				"fixed top-0 left-0 z-50 mt-16 flex h-screen flex-col gap-6 border-r border-gray-200 bg-white font-inter text-gray-900 transition-all duration-300 ease-in-out lg:mt-0 dark:border-slate-700 dark:bg-slate-800 dark:text-zinc-100",
				showLabels ? "w-[256px]" : "w-[64px]",
				isMobileOpen ? "translate-x-0" : "-translate-x-full",
				"lg:translate-x-0",
			)}
		>
			<div className="p-2">
				<Link
					to="/"
					className={cn(
						"flex h-12 items-center gap-2 rounded-[4px]",
						showLabels ? "px-2" : "justify-center",
					)}
				>
					<img
						className="dark:hidden"
						src="/images/logo/credito-logo-colored.svg"
						alt="Logo"
						width={25}
						height={28}
					/>
					<img
						className="hidden dark:block"
						src="/images/logo/credito-logo-light.svg"
						alt="Logo"
						width={25}
						height={28}
					/>
					{showLabels && (
						<span className="font-jakarta text-[14.821px] leading-[1.1] font-extrabold tracking-[-0.02em] whitespace-nowrap text-gray-900 dark:text-white">
							CréditoHipotecar.io
						</span>
					)}
				</Link>
			</div>

			<nav className="no-scrollbar overflow-y-auto p-2">
				<ul className="flex flex-col gap-1">
					{navItems.filter((nav) => nav.enabled?.() ?? true).map((nav) => {
						const label = t(nav.nameKey);
						const active = isActive(nav);
						return (
							<li key={nav.nameKey}>
								<Link
									to={nav.path}
									onClick={handleMobileLinkClick}
									aria-current={active ? "page" : undefined}
									aria-label={showLabels ? undefined : label}
									title={showLabels ? undefined : label}
									className={cn(
										"flex items-center gap-2 rounded-[4px] text-sm/5 transition-colors [&_svg]:size-4 [&_svg]:shrink-0",
										showLabels ? "h-[52px] px-2" : "size-12 justify-center",
										active
											? "bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-zinc-100"
											: "text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-zinc-100 dark:hover:bg-slate-700/50",
									)}
								>
									{nav.icon}
									{showLabels && <span className="min-w-0 truncate">{label}</span>}
								</Link>
							</li>
						);
					})}
				</ul>
			</nav>
		</aside>
	);
};

export default AppSidebar;
